import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { clearCombo } from "../slices/comboSlice";
import ComboMenuModal from "./ComboMenuModal";
import { toast } from "react-toastify";
import "./ComboModern.css";
import { useSettings } from "../context/SettingsContext";
const ComboModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const combo = useSelector((s) => s.combo.combo);
const { settings } = useSettings();
  const editComboItem = JSON.parse(localStorage.getItem("editComboItem"));
  const isEditing = !!editComboItem;

  const [openCategory, setOpenCategory] = useState(null);
  const [menuSlots, setMenuSlots] = useState(() => {
    if (editComboItem?.selections) return editComboItem.selections;
    return {};
  });
  const [activeSlot, setActiveSlot] = useState(null);
  const [editSlotData, setEditSlotData] = useState(null); // ✅ new
  const [comboQuantity, setComboQuantity] = useState(editComboItem?.quantity || 1);
  const [visibleSlots, setVisibleSlots] = useState({});

  // ✅ Fix: Calculate visible slots correctly even if slots are non-sequential
  useEffect(() => {
    if (!combo || !combo.category_data) return;

    const initialVisibility = {};
    combo.category_data.forEach((cat, idx) => {
      const slotMap = menuSlots[idx]?.items || {};
      const usedIndexes = Object.keys(slotMap).map((i) => parseInt(i, 10));
      const maxUsed = usedIndexes.length ? Math.max(...usedIndexes) + 1 : 0;
      const visibleCount = Math.min(Math.max(2, maxUsed + 1), cat.max);
      initialVisibility[idx] = visibleCount;
    });

    setVisibleSlots(initialVisibility);
  }, [combo, menuSlots]);

  if (!combo) return null;

  const selectedCategoryCount = Object.keys(menuSlots).length;

  const handleMenuSave = (categoryIndex, slotIndex, menuData) => {
    const cat = combo.category_data[categoryIndex];
    const maxAllowed = cat.max || 1;
    const totalQtyInCat = Object.values(menuSlots[categoryIndex]?.items || {}).reduce(
      (sum, m) => sum + (m.quantity || 1),
      0
    );

    if (totalQtyInCat + (menuData.quantity || 1) > maxAllowed) {
      toast.error(`You can select maximum ${maxAllowed} item(s) in "${cat.name}"`);
      return;
    }

    const categoryAlreadyUsed = !!menuSlots[categoryIndex];
    if (!categoryAlreadyUsed && selectedCategoryCount >= (combo.max_category_sel || Infinity)) {
      toast.error(
        `You can only select from ${combo.max_category_sel} category${
          combo.max_category_sel > 1 ? "ies" : ""
        }.` 
      );
      return;
    }

    setMenuSlots((prev) => {
      const copy = { ...prev };
      if (!copy[categoryIndex]) {
        copy[categoryIndex] = {
          categoryId: cat.id,
          categoryName: cat.name,
          items: {},
        };
      }
      copy[categoryIndex].items[slotIndex] = menuData;
      return copy;
    });

    // Expand visible slots if needed
    setVisibleSlots((prev) => ({
      ...prev,
      [categoryIndex]: Math.min(
        Math.max(prev[categoryIndex] || 2, slotIndex + 2),
        cat.max
      ),
    }));

    setActiveSlot(null);
    setEditSlotData(null); // ✅ reset
    toast.success(`${menuData.name} added`);
  };

  const handleRemoveSlot = (categoryIndex, slotIndex) => {
    setMenuSlots((prev) => {
      const copy = { ...prev };
      if (copy[categoryIndex]?.items) {
        delete copy[categoryIndex].items[slotIndex];
        if (Object.keys(copy[categoryIndex].items).length === 0)
          delete copy[categoryIndex];
      }
      return copy;
    });

    setVisibleSlots((prev) => ({
      ...prev,
      [categoryIndex]: Math.max(prev[categoryIndex] || 2, 2),
    }));
  };

  const computeTotal = () => {
    let totalMenuPrice = 0;
    Object.values(menuSlots).forEach((catBlock) => {
      Object.values(catBlock.items || {}).forEach((m) => {
        const menuBase =
          Number(m.basePrice || m.price || 0) * Number(m.quantity || 1);
        const modifierTotal = (m.selectedModifiers || []).reduce(
          (sum, opt) => sum + Number(opt.price || 0) * Number(opt.quantity || 0),
          0
        );
        totalMenuPrice += menuBase + modifierTotal;
      });
    });
    const comboBase = Number(combo.price || 0);
    return (comboBase + totalMenuPrice) * Number(comboQuantity || 1);
  };

  const handleAddToCart = () => {
    const minCatSel = combo.min_category_sel || 1;
    const maxCatSel = combo.max_category_sel || combo.category_data.length;

    if (selectedCategoryCount < minCatSel) {
      toast.error(`Please select at least ${minCatSel} category${minCatSel > 1 ? "ies" : "y"}.`);
      return;
    }

    if (selectedCategoryCount > maxCatSel) {
      toast.error(`You can select up to ${maxCatSel} category${maxCatSel > 1 ? "ies" : "y"}.`);
      return;
    }

    for (let i = 0; i < combo.category_data.length; i++) {
      const cat = combo.category_data[i];
      const slotMap = menuSlots[i]?.items || null;
      if (!slotMap || Object.keys(slotMap).length === 0) continue;

      const totalQty = Object.values(slotMap).reduce(
        (sum, m) => sum + (m.quantity || 1),
        0
      );

      if (totalQty < (cat.min || 0)) {
        toast.error(`Please select at least ${cat.min} item(s) in "${cat.name}"`);
        return;
      }

      if (totalQty > cat.max) {
        toast.error(`You can select maximum ${cat.max} item(s) in "${cat.name}"`);
        return;
      }
    }

    const comboCartItem = {
      id: isEditing ? editComboItem.id : Date.now(),
      type: "combo",
      comboId: combo.id || combo.combo_id || null,
      name: combo.name,
      image: combo.image || null,
      basePrice: combo.price,
      quantity: comboQuantity,
      selections: menuSlots,
      total: computeTotal(),
      exclusion: combo?.exclusion
    };

    const existing = JSON.parse(localStorage.getItem("cart")) || [];
    if (isEditing) {
      const updatedCart = existing.map((item) =>
        item.id === editComboItem.id ? comboCartItem : item
      );

        const cc = JSON.parse(localStorage.getItem('cart')) || [];
        if (cc.length === 0) {
          localStorage.setItem("cartCreatedAt", Date.now().toString());
        }
        
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      localStorage.setItem("last_activity_time", Date.now().toString());
      toast.success("Combo updated successfully!");
      localStorage.removeItem("editComboItem");
    } else {
      localStorage.setItem("cart", JSON.stringify([...existing, comboCartItem]));
      localStorage.setItem("last_activity_time", Date.now().toString());
      toast.success("Combo added to cart!");
    }

    dispatch(clearCombo());
    onClose();
  };

  // ✅ open slot with optional existing menu data
  const openSlot = (categoryIndex, slotIndex, existingMenu = null) => {
    setActiveSlot({ categoryIndex, slotIndex });
    setEditSlotData(existingMenu);
  };

  const handleClose = () => {
    localStorage.removeItem("editComboItem");
    dispatch(clearCombo());
    onClose();
  };

  const maxReached = selectedCategoryCount >= (combo.max_category_sel || Infinity);
  const progressPercent = Math.min(
    (selectedCategoryCount / (combo.max_category_sel || 1)) * 100,
    100
  );

  return (
    <div className="combo-modern-wrapper" role="dialog" aria-modal="true">
      <div className={`combo-modern-container ${isEditing ? "editing-mode" : ""}`}>
        <header className="cm-header enhanced">
          <button className="cm-close top-right" onClick={handleClose}>✕</button>

          <div className="cm-header-left">
            {combo.image ? (
              <img src={combo.image} alt={combo.name} className="cm-header-thumb" />
            ) : (
              <div className="cm-header-thumb placeholder"  style={{ background: settings?.cart_title_color || "#111827" }}>🍽️</div>
            )}

            <div className="cm-title-block">
              <h2 className="cm-title"  style={{ color: settings?.cart_title_color || "#111827" }}>{combo.name}</h2>
              <div className="cm-sub">
                <span className="cm-price">
                  {localStorage.getItem('currency')} {Number(combo.price || 0).toFixed(2)}
                </span>
                <span className="cm-note">
                  • Select {combo.min_category_sel}–{combo.max_category_sel} categories
                </span>
              </div>

              <div className="cm-cat-progress-bar">
                <div className="cm-cat-progress-fill" style={{ width: `${progressPercent}%` }}></div>
              </div>
              <div className="cm-cat-progress-text">
                <strong>{selectedCategoryCount}</strong> /{" "}
                <strong>{combo.max_category_sel}</strong> categories selected
              </div>
            </div>
          </div>
        </header>

        {/* BODY */}
        <div className="cm-body">
          {combo.category_data.map((cat, idx) => {
            const slotMap = menuSlots[idx]?.items || {};
            const totalQty = Object.values(slotMap).reduce(
              (sum, m) => sum + (m.quantity || 1),
              0
            );
            const categoryUsed = !!Object.keys(slotMap).length;
            const reachedLimit = totalQty >= cat.max;
            const isDisabled = maxReached && !categoryUsed;
            const totalSlots = Math.min(visibleSlots[idx] || 2, cat.max);

            return (
              <section key={idx} className={`cm-category ${isDisabled ? "disabled" : ""}`}>
                <div
                  className="cm-category-head"
                  onClick={() => !isDisabled && setOpenCategory(openCategory === idx ? null : idx)}
                  role="button"
                >
                  <div className="cm-cat-info">
                    <div className="cm-cat-name">
                      {cat.name}{" "}
                      {reachedLimit && <span className="cm-full-badge">Full</span>}
                    </div>
                    <div className="cm-cat-range">
                      Pick {cat.min}–{cat.max} • ({totalQty}/{cat.max})
                      {categoryUsed && (
                        <span className="cm-tick-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="cm-cat-status">
                    <div className={`cm-arrow ${openCategory === idx ? "open" : ""}`}>⌄</div>
                  </div>
                </div>

                {openCategory === idx && (
                  <div className="cm-category-body">
                    <div className="cm-slots">
                      {[...Array(totalSlots)].map((_, slotIndex) => {
                        const filled = slotMap[slotIndex];
                        return (
                          <div key={slotIndex} className="cm-slot-wrap">
                            <button
                              className={`cm-slot ${filled ? "filled" : ""}`}
                              onClick={() => {
                                if (filled) {
                                  // ✅ reopen modal with preselected item
                                  openSlot(idx, slotIndex, filled);
                                } else {
                                  if (totalQty >= cat.max) {
                                    toast.error(
                                      `You’ve reached the max quantity (${cat.max}) for "${cat.name}".`
                                    );
                                    return;
                                  }
                                  openSlot(idx, slotIndex);
                                }
                              }}
                              disabled={isDisabled && !filled}
                            >
                              {!filled ? (
                                <div className="cm-plus">+</div>
                              ) : (
                                <div className="cm-filled">
                                  <img src={filled.image} alt={filled.name} className="cm-thumb" />
                                  <div className="cm-filled-meta">
                                    <div className="cm-filled-name">{filled.name.substring(0, 20)}</div>
                                    <div className="cm-filled-price">
                                      {localStorage.getItem('currency')} {(filled.total || 0).toFixed(2)} × {filled.quantity || 1}
                                    </div>
                                  </div>
                                  <button
                                    className="cm-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveSlot(idx, slotIndex);
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                            </button>
                          </div>
                        );
                      })}

                      {cat.max > totalSlots && (
                        <div className="cm-add-more-wrap">
                          <button
                            className="cm-add-more-btn cm-add"
                            onClick={() =>
                              setVisibleSlots((prev) => ({
                                ...prev,
                                [idx]: Math.min((prev[idx] || 2) + 1, cat.max),
                              }))
                            }
                          >
                            + Add
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <footer className="cm-footer">
          <div className="cm-qty">
            <button 
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} onClick={() => setComboQuantity(Math.max(1, comboQuantity - 1))}>−</button>
            <span>{comboQuantity}</span>
            <button  
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} onClick={() => setComboQuantity(comboQuantity + 1)}>+</button>
          </div>

          <button className="cm-add" 
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} onClick={handleAddToCart}>
            {isEditing ? "UPDATE COMBO" : "ADD TO CART"} • {localStorage.getItem('currency')} {computeTotal().toFixed(2)}
          </button>
        </footer>
      </div>

      {activeSlot && (
        <ComboMenuModal
          category={combo.category_data[activeSlot.categoryIndex]}
          onSave={(menuData) =>
            handleMenuSave(activeSlot.categoryIndex, activeSlot.slotIndex, menuData)
          }
          onClose={() => {
            setActiveSlot(null);
            setEditSlotData(null);
          }}
          initialData={editSlotData} // ✅ send preselected data
        />
      )}
    </div>
  );
};

export default ComboModal;
